import { useEffect, useState } from "react";
import { FiDownload } from "react-icons/fi";
import Badge from "../common/Badge";
import Button from "../common/Button";
import ReadableSummary from "../common/ReadableSummary";
import {
  downloadTranscript,
  hasTranscript,
} from "../../utils/callDisplay";
import {
  buildCallSummaryText,
  getCallDirectionMeta,
  getCallStatusMeta,
  parseTranscript,
} from "../../utils/helpers";
import {
  formatAppointmentService,
  formatDateTime,
  formatDuration,
  formatPhone,
} from "../../utils/formatters";
import BaseModal from "./BaseModal";

function TranscriptBody({ transcript }) {
  const parsed = parseTranscript(transcript);

  if (parsed.type === "raw") {
    return (
      <pre className="transcript-raw bg-gray-50 p-4 rounded-lg text-[15px] font-mono whitespace-pre-wrap break-words">
        {parsed.text}
      </pre>
    );
  }

  return (
    <div className="transcript-lines space-y-3 max-h-[400px] overflow-y-auto pr-2">
      {parsed.items.map((item, index) => (
        <div
          className="t-line flex gap-3 py-2 border-b border-gray-100 last:border-0"
          key={`${item.speaker}-${index}`}
        >
          <span
            className={`t-speaker ${item.speakerClass} font-semibold min-w-[85px] text-[15px]`.trim()}
          >
            {item.speaker}
          </span>
          <span className="t-text text-gray-800 text-[15px] leading-relaxed flex-1">
            {item.text}
          </span>
        </div>
      ))}
    </div>
  );
}

function SummaryField({ label, value }) {
  if (!value) {
    return null;
  }

  return (
    <div className="space-y-1">
      <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
        {label}
      </div>
      <div className="text-gray-800 text-[15px] leading-relaxed">{value}</div>
    </div>
  );
}

function getDirectionLabel(call = {}) {
  const normalized = String(
    call.call_direction || call.direction || "",
  ).toLowerCase();

  if (normalized === "outbound") {
    return "Outbound";
  }

  if (normalized === "inbound") {
    return "Inbound";
  }

  return "N/A";
}

function getConfirmationTagClass(tone) {
  if (tone === "positive") {
    return "bg-green-50 text-green-700";
  }

  if (tone === "negative") {
    return "bg-red-50 text-red-700";
  }

  return "bg-blue-50 text-blue-700";
}

function CallSummarySection({ call, showCallMetadata = false }) {
  if (!call.summary && !showCallMetadata) {
    return null;
  }

  return (
    <div className="modal-summary-block bg-gray-50 rounded-lg p-4">
      <div className="modal-summary-label text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
        Call Summary
      </div>
      <div className="space-y-4">
        <ReadableSummary summary={call.summary} />

        {showCallMetadata ? (
          <div className="space-y-4">
            <SummaryField label="Caller Name" value={call.caller_name || "N/A"} />
            <SummaryField
              label="Phone Number"
              value={formatPhone(call.phone_number)}
            />
            <SummaryField
              label="Date & Time"
              value={formatDateTime(call.created_at)}
            />
            <SummaryField
              label="Duration"
              value={formatDuration(call.duration_seconds)}
            />
            <SummaryField
              label="Call Direction"
              value={getDirectionLabel(call)}
            />
            <SummaryField
              label="Service Type"
              value={formatAppointmentService(call.service_type)}
            />
            <SummaryField
              label="Booking Status"
              value={call.booking_status || "N/A"}
            />
            <SummaryField
              label="Confirmation Number"
              value={call.confirmation_number || "N/A"}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CallHistoryRecord({ call, onDownloadTranscript }) {
  const callStatus = getCallStatusMeta(call);
  const direction = getCallDirectionMeta(call);
  const summaryTag = buildCallSummaryText(call);
  const transcriptAvailable = hasTranscript(call);

  const handleDownload = () => {
    if (onDownloadTranscript) {
      onDownloadTranscript(call);
      return;
    }

    downloadTranscript(call);
  };

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[16px] font-semibold text-gray-900">
            {call.__historyLabel || "Call Record"}
          </div>
          <div className="text-sm text-gray-500">
            {call.caller_name || "Unknown caller"} - {formatPhone(call.phone_number)}
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {formatDateTime(call.created_at)}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[14px] text-gray-600">
        <Badge variant={callStatus.variant}>
          {callStatus.icon} {callStatus.label}
        </Badge>
        <Badge variant={direction.variant}>
          {direction.icon} {direction.label}
        </Badge>
      </div>

      {summaryTag ? (
        <div
          className={`confirmation-tag ${summaryTag.tone} p-3 rounded-lg text-[15px] font-medium ${getConfirmationTagClass(summaryTag.tone)}`.trim()}
        >
          {summaryTag.text}
        </div>
      ) : null}

      <CallSummarySection call={call} showCallMetadata />

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
            Transcript
          </div>
          {/* <Button
            variant="primary"
            onClick={handleDownload}
            className="w-full sm:w-auto"
            disabled={!transcriptAvailable}
          >
            <FiDownload />
            <span>Download Transcript</span>
          </Button> */}
        </div>

        {transcriptAvailable ? (
          <TranscriptBody transcript={call.transcript} />
        ) : (
          <div className="no-transcript text-center py-6 text-gray-400 text-[15px]">
            No transcript available for this call.
          </div>
        )}
      </div>
    </div>
  );
}

function CallHistorySelector({
  calls,
  selectedCallIndex,
  onSelectCall,
}) {
  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
        Follow-up History
      </div>
      <div className="flex flex-wrap gap-2">
        {calls.map((historyCall, index) => {
          const direction = getCallDirectionMeta(historyCall);
          const status = getCallStatusMeta(historyCall);
          const isSelected = index === selectedCallIndex;

          return (
            <button
              key={
                historyCall.__historyKey ||
                historyCall.id ||
                historyCall.call_id ||
                historyCall.created_at
              }
              type="button"
              onClick={() => onSelectCall(index)}
              className={`rounded-lg border px-3 py-2 text-left shadow-sm transition ${
                isSelected
                  ? "border-slate-300 bg-slate-50 text-slate-900"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
              }`.trim()}
            >
              <div className="text-sm font-medium">
                {index === 0 ? "Call 1 - Latest" : `Call ${index + 1}`}
              </div>
              <div className="text-xs text-slate-500">
                {formatDateTime(historyCall.created_at)}
              </div>
              <div className="text-xs text-slate-500">
                {direction.label} - {status.label}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function TranscriptModal({
  open,
  call,
  onClose,
  onCallAgain,
  showCallMetadata = false,
  onDownloadTranscript,
}) {
  const safeCall = call || {};
  const historyItems =
    Array.isArray(safeCall.callHistory) && safeCall.callHistory.length
      ? safeCall.callHistory
      : [safeCall];
  const isGroupedHistory = historyItems.length > 1;
  const [selectedCallIndex, setSelectedCallIndex] = useState(0);
  const callStatus = getCallStatusMeta(safeCall);
  const direction = getCallDirectionMeta(safeCall);
  const summaryTag = buildCallSummaryText(safeCall);
  const transcriptAvailable = hasTranscript(safeCall);
  const formattedPhone = formatPhone(safeCall.phone_number);
  const historyTitleValue =
    safeCall.caller_name ||
    (formattedPhone !== "--" ? formattedPhone : "Unknown caller");
  const activeCall = historyItems[selectedCallIndex] || historyItems[0] || safeCall;

  useEffect(() => {
    if (open) {
      setSelectedCallIndex(0);
    }
  }, [open, safeCall.__modalRequestKey]);

  useEffect(() => {
    if (selectedCallIndex >= historyItems.length) {
      setSelectedCallIndex(0);
    }
  }, [historyItems.length, selectedCallIndex]);

  if (!call) {
    return null;
  }

  const handleDownload = () => {
    if (onDownloadTranscript) {
      onDownloadTranscript(activeCall);
      return;
    }

    downloadTranscript(activeCall);
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={
        isGroupedHistory
          ? `Call History - ${historyTitleValue}`
          : `${formattedPhone}${call.caller_name ? ` - ${call.caller_name}` : ""}`
      }
      meta={
        isGroupedHistory ? (
          <div className="flex flex-wrap items-center gap-2 text-[14px] text-gray-600">
            {formattedPhone} &middot; {historyItems.length} interaction
            {historyItems.length === 1 ? "" : "s"}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 text-[14px] text-gray-600">
            {formatDateTime(call.created_at)} &middot; Duration:{" "}
            {formatDuration(call.duration_seconds)} &middot;{" "}
            <Badge variant={callStatus.variant}>
              {callStatus.icon} {callStatus.label}
            </Badge>{" "}
            <Badge variant={direction.variant}>
              {direction.icon} {direction.label}
            </Badge>
          </div>
        )
      }
      modalId="modal"
    >
      <div className="space-y-5">
        {isGroupedHistory ? (
          <div className="space-y-4">
            <CallHistorySelector
              calls={historyItems}
              selectedCallIndex={selectedCallIndex}
              onSelectCall={setSelectedCallIndex}
            />
            <CallHistoryRecord
              call={activeCall}
              onDownloadTranscript={onDownloadTranscript}
            />
          </div>
        ) : (
          <>
            {summaryTag ? (
              <div
                className={`confirmation-tag ${summaryTag.tone} p-3 rounded-lg text-[15px] font-medium ${getConfirmationTagClass(summaryTag.tone)}`.trim()}
              >
                {summaryTag.text}
              </div>
            ) : null}

            <CallSummarySection
              call={call}
              showCallMetadata={showCallMetadata}
            />

            {transcriptAvailable ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="transcript-label text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    Full Transcript
                  </div>
                  {/* <Button
                    variant="primary"
                    onClick={handleDownload}
                    className="w-full sm:w-auto"
                  >
                    <FiDownload />
                    <span>Download Transcript</span>
                  </Button> */}
                </div>
                <TranscriptBody transcript={call.transcript} />
              </>
            ) : (
              <div className="no-transcript text-center py-8 text-gray-400 text-[15px]">
                No transcript available for this call.
              </div>
            )}
          </>
        )}

        <div className="modal-actions pt-4 border-t border-gray-200 mt-4">
          <Button
            variant="green"
            onClick={() =>
              onCallAgain(
                activeCall.phone_number || "",
                activeCall.caller_name || "",
              )
            }
            className="w-full sm:w-auto"
          >
            Call Again
          </Button>
        </div>
      </div>
    </BaseModal>
  );
}
